import React, { useEffect, useRef, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { aiSuggestions } from '../../mock/mock';
import { Send, Sparkles, Heart, Loader2 } from 'lucide-react';

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I’m your wedding friend. Ask me anything — budget, vendors, etiquette, wording. I won’t judge.' },
  ]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || typing) return;
    setMessages((m) => [...m, { role: 'user', text: value }]);
    setInput('');
    setTyping(true);
    try {
      const { data } = await client.post('/ai/chat', { sessionId, message: value });
      if (!sessionId) setSessionId(data.sessionId);
      setMessages((m) => [...m, { role: 'ai', text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'ai', text: 'I’m having a tiny moment — please try again.' }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <div className="animate-float-up">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-[#F4D6CE] flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-[#7B2C3B]" />
        </div>
        <div>
          <h1 className="font-serif text-3xl text-[#2A2A2A] flex items-center gap-3"><AppIcon feature="ai" size="lg" reveal testId="section-icon-ai" />Your wedding friend</h1>
          <p className="text-xs text-[#6A6A6A]">Powered by GPT-5 · knows your couple, your dates, and your budget.</p>
        </div>
      </div>

      <div className="soft-card rounded-2xl flex flex-col h-[640px] mt-4 overflow-hidden">
        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gradient-to-b from-[#FBF7F2] to-[#F6EAE0]/40">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#7B2C3B] text-[#FBF7F2] rounded-br-sm' : 'bg-white border border-[#EFE3D8] text-[#2A2A2A] rounded-bl-sm'}`}>
                {m.role === 'ai' && (
                  <span className="flex items-center gap-1.5 mb-1 text-xs text-[#B8896C]">
                    <Heart className="w-3 h-3 fill-[#B8896C]" /> wedding friend
                  </span>
                )}
                {m.text}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-white border border-[#EFE3D8] rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-[#6A6A6A] inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>thinking…</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="px-5 pt-3 flex flex-wrap gap-2 border-t border-[#EFE3D8] bg-[#FBF7F2]">
          {aiSuggestions.map((s, i) => (
            <button key={i} onClick={() => send(s)} disabled={typing} className="text-xs px-3 py-1.5 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] transition-colors disabled:opacity-50">
              {s}
            </button>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-[#EFE3D8] bg-[#FBF7F2] flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask me anything about your wedding…" className="flex-1 px-4 py-3 rounded-full border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]" />
          <button type="submit" disabled={typing} className="btn-primary w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-60"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
}
